import React, { createContext, useContext, useEffect, useState } from 'react';


// Theme Color Palettes
const THEME_PALETTES = {
  light: {
    name: 'Light',
    bg: '#FFFFFF',
    bgSecondary: '#F8F9FA',
    bgTertiary: '#E9ECEF',
    text: '#1A1A1A',
    textSecondary: '#6B7280',
    primary: '#6366F1',
    primaryLight: '#E0E7FF',
    primaryDark: '#4F46E5',
    accent: '#EC4899',
    border: '#E5E7EB',
    shadow: 'rgba(0,0,0,0.08)'
  },
  dark: {
    name: 'Dark',
    bg: '#0F172A',
    bgSecondary: '#1E293B',
    bgTertiary: '#334155',
    text: '#F1F5F9',
    textSecondary: '#CBD5E1',
    primary: '#818CF8',
    primaryLight: '#4F46E5',
    primaryDark: '#A5B4FC',
    accent: '#F472B6',
    border: '#475569',
    shadow: 'rgba(0,0,0,0.3)'
  },
  vibrant: {
    name: 'Vibrant',
    bg: '#0D0221',
    bgSecondary: '#370447',
    bgTertiary: '#6A0572',
    text: '#F5F5F5',
    textSecondary: '#E0E0E0',
    primary: '#FF006E',
    primaryLight: '#FB5607',
    primaryDark: '#FFBE0B',
    accent: '#00F5FF',
    border: '#8338EC',
    shadow: 'rgba(255,0,110,0.2)'
  },
  cool: {
    name: 'Cool',
    bg: '#E0F4FF',
    bgSecondary: '#C7E9F5',
    bgTertiary: '#90E0EF',
    text: '#00355F',
    textSecondary: '#0E5A6F',
    primary: '#0096C7',
    primaryLight: '#00B4D8',
    primaryDark: '#0077B6',
    accent: '#00D9FF',
    border: '#90E0EF',
    shadow: 'rgba(0,150,199,0.15)'
  },
  warm: {
    name: 'Warm',
    bg: '#FFF8F3',
    bgSecondary: '#FFE8D6',
    bgTertiary: '#FFCAB0',
    text: '#5D3A1A',
    textSecondary: '#8B4513',
    primary: '#E85D04',
    primaryLight: '#FB8500',
    primaryDark: '#D62828',
    accent: '#F77F00',
    border: '#FCBF49',
    shadow: 'rgba(232,93,4,0.15)'
  },
  nature: {
    name: 'Nature',
    bg: '#F1FAEE',
    bgSecondary: '#E8F5E9',
    bgTertiary: '#C8E6C9',
    text: '#1B5E20',
    textSecondary: '#2E7D32',
    primary: '#2E7D32',
    primaryLight: '#66BB6A',
    primaryDark: '#1B5E20',
    accent: '#26A69A',
    border: '#A5D6A7',
    shadow: 'rgba(46,125,50,0.15)'
  }
};

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [mode, setMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('themeMode') || 'light';
    }
    return 'light';
  });

  const [palette, setPalette] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('themePalette') || 'light';
    }
    return 'light';
  });

  const currentTheme = THEME_PALETTES[palette];

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
    localStorage.setItem('themePalette', palette);
  }, [mode, palette]);

  useEffect(() => {
    const root = document.documentElement;
    Object.entries(currentTheme).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });
  }, [currentTheme]);

  const toggleMode = () => {
    setMode(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ mode, palette, setPalette, toggleMode, colors: currentTheme, palettes: THEME_PALETTES }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;